import { autorun } from "mobx";
import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { CountryWithStateModel } from "./CountryModels";
import {
  ApplicationStatus,
  MerchantAccountsGateway,
  NMIProcessorTypes,
} from "../enums";
import { GeneralSettingsModel } from "./GeneralSettingsModel";
import {
  IAPICreateMerchantAccountModel,
  IAPICreateMerchantModel,
  IAPIMerchantVolumeModel,
} from "../interfaces/IAPICreateMerchantModel";
import {
  IAPIMerchantAccountModel,
  IAPIMerchantPaymentModel,
  IAPIMerchantWithPaySettingsModel,
  IAPIPurchaseMerchantModel,
  IAPISurchargesModel,
} from "../interfaces/IAPIMerchantModels";
import {
  IAPIGeneralSettingsModel,
  IAPIMerchantAllowedPaymentMethodTypes,
  IAPIPaymentSetting,
} from "../interfaces/PaymentSettingsInterfaces";
import { PaymentModuleUtility } from "../utils/PaymentModuleUtility";
import {
  IAPIMCCCodeResponseModel,
  IAPIMerchantPaymentMethodDetails,
  IAPIMerchantPaymentSettings,
} from "../interfaces";

import TSYSLogo from "../../../../../images/logos/tsys-logo.svg";
import ElavonLogo from "../../../../../images/logos/elavon-logo.svg";
import GlobalPaymentsLogo from "../../../../../images/logos/global-payments-logo.svg";
import FiservLogo from "../../../../../images/logos/fiserv-logo.svg";
import MindMePaymentsLogo from "../../../../../images/logos/mindme-payment-logo.svg";

export class MerchantAllowedPaymentMethodTypes {
  applePay: boolean;
  googlePay: boolean;
  bankAch: boolean;
  checkWire: boolean;
  creditCard: boolean;

  constructor(data?: Partial<MerchantAllowedPaymentMethodTypes>) {
    this.applePay = data?.applePay ?? false;
    this.googlePay = data?.googlePay ?? false;
    this.bankAch = data?.bankAch ?? false;
    this.checkWire = data?.checkWire ?? false;
    this.creditCard = data?.creditCard ?? false;
  }

  static deserializePaymentMethodTypes(
    data: IAPIMerchantAllowedPaymentMethodTypes,
  ): MerchantAllowedPaymentMethodTypes {
    return new MerchantAllowedPaymentMethodTypes({
      applePay: data.applePay,
      googlePay: data.googlePay,
      bankAch: data.bankAch,
      checkWire: data.checkWire,
      creditCard: true,
    });
  }

  createAPIModel(): IAPIMerchantAllowedPaymentMethodTypes {
    return {
      visa: true,
      masterCard: false,
      discover: false,
      americanExpress: false,
      applePay: this.applePay,
      googlePay: this.googlePay,
      bankAch: this.bankAch,
      checkWire: this.checkWire,
    };
  }
}

export class MerchantApplicationInputModel {
  businessId: string;
  businessName: string;
  accountName: string;
  address: AddressInputModel;
  accountDetails: MerchantAccountDetails;
  volumeDetails: ApplicationVolumeDetails;
  modeOfTransactions: ModeOfTransactions;
  errors: InputErrorModel<MerchantApplicationInputModel>[] = [];
  constructor(data?: Partial<MerchantApplicationInputModel>) {
    this.businessId = data?.businessId || "";
    this.businessName = data?.businessName || "";
    this.accountName = data?.accountName || "";
    this.address = new AddressInputModel(data?.address);
    this.accountDetails = new MerchantAccountDetails(data?.accountDetails);
    this.volumeDetails = new ApplicationVolumeDetails(data?.volumeDetails);
    this.modeOfTransactions = new ModeOfTransactions(data?.modeOfTransactions);
    this.errors =
      (data &&
        data?.errors?.map((e) => {
          return new InputErrorModel<MerchantApplicationInputModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof MerchantApplicationInputModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof MerchantApplicationInputModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<MerchantApplicationInputModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.businessName,
      "businessName",
      newErrors,
      undefined,
      "Please enter business name",
    );
    CoreInputModelUtil.validateTextField(
      this.accountName,
      "accountName",
      newErrors,
      undefined,
      "Please enter account name",
    );
    const isValidAddress = this.address.validate(isAutoRun);
    const isValidAccount = this.accountDetails.validate(isAutoRun);
    const isValidVolume = this.volumeDetails.validate(isAutoRun);
    const isValidMode = this.modeOfTransactions.validate(isAutoRun);

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;

    return (
      this.errors.length === 0 &&
      isValidAddress &&
      isValidAccount &&
      isValidVolume &&
      isValidMode
    );
  }

  createFiserveAPIModel(): IAPICreateMerchantModel {
    return {
      name: this.accountName,
      businessId: this.businessId,
      accountDetails: this.accountDetails.createAPIModel(),
      address: this.address.createAPIModel(),
      transactionModes: this.modeOfTransactions.createAPIModel(),
      volumeDetails: this.volumeDetails.createAPIModel(),
    } as IAPICreateMerchantModel;
  }

  createAPIModel(): IAPICreateMerchantAccountModel {
    return {
      accountId: AuthService.authData.accountId,
      source: "Fiserv",
      fiservApplication: this.createFiserveAPIModel(),
      NMIApplication: null,
    } as IAPICreateMerchantAccountModel;
  }
}

export class AddressInputModel {
  countryDetails: CountryWithStateModel;
  city: string;
  zip: string;
  address1: string;
  phoneNumber: string;
  errors: InputErrorModel<AddressInputModel>[];

  constructor(data?: Partial<AddressInputModel>) {
    this.city = data?.city || "";
    this.zip = data?.zip || "";
    this.address1 = data?.address1 || "";
    this.phoneNumber = data?.phoneNumber || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.errors =
      (data &&
        data.errors?.map((e) => new InputErrorModel<AddressInputModel>(e))) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof AddressInputModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof AddressInputModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<AddressInputModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.city,
      "city",
      newErrors,
      undefined,
      "Please enter city",
    );

    if (this.city) {
      const isValidCity = PaymentModuleUtility.isAlphaBetical(this.city);
      if (!isValidCity) {
        CoreInputModelUtil.addError(
          "city",
          newErrors,
          "Only alphabets allowed",
        );
      }
    }
    AddressInputModel.validateZip(this.zip, "zip", newErrors);
    CoreInputModelUtil.validateTextField(
      this.address1,
      "address1",
      newErrors,
      undefined,
      "Please enter address",
    );
    CoreInputModelUtil.validatePhoneNumber(
      "phoneNumber",
      this.phoneNumber,
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.countryDetails.abbreviationName.toString(),
      "countryDetails",
      newErrors,
      undefined,
      "please select country",
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  static validateZip = (
    value: string,
    field: keyof AddressInputModel,
    newErrors: InputErrorModel<AddressInputModel>[],
  ): void => {
    if (!value) {
      newErrors.push(
        new InputErrorModel<AddressInputModel>({
          field: field,
          message: "Please enter zip",
        }),
      );
    } else if (!/^\d+$/.test(value)) {
      newErrors.push(
        new InputErrorModel<AddressInputModel>({
          field: field,
          message: "Zip code must be a number",
        }),
      );
    } else if (value.length > 5) {
      newErrors.push(
        new InputErrorModel<AddressInputModel>({
          field: field,
          message: "maximum length should be 5 digits",
        }),
      );
    }
  };

  createAPIModel(): any {
    return {
      country: this.countryDetails.abbreviationName || "",
      countryId: this.countryDetails.id,
      city: this.city,
      zipCode: this.zip,
      address1: this.address1,
      countryCode: this.countryDetails.code.toString(),
    };
  }
}
export class ApplicationVolumeDetails {
  avgAnnualVolume: string;
  avgTicketAmount: string;
  highestTicketAmount: string;
  errors: InputErrorModel<ApplicationVolumeDetails>[];

  constructor(data?: Partial<ApplicationVolumeDetails>) {
    this.avgAnnualVolume = data?.avgAnnualVolume || "";
    this.avgTicketAmount = data?.avgTicketAmount || "";
    this.highestTicketAmount = data?.highestTicketAmount || "";
    this.errors =
      (data &&
        data.errors?.map(
          (e) => new InputErrorModel<ApplicationVolumeDetails>(e),
        )) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof ApplicationVolumeDetails): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof ApplicationVolumeDetails): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  static validateAvgAnnualVolume(
    field: keyof ApplicationVolumeDetails,
    avgAnnualVolume: string,
    newErrors: InputErrorModel<ApplicationVolumeDetails>[],
  ): void {
    const value = parseFloat(avgAnnualVolume);
    if (isNaN(value)) {
      newErrors.push(
        new InputErrorModel<ApplicationVolumeDetails>({
          field: field,
          message: "Please enter a valid number",
        }),
      );
    } else if (value < 1000 || value > 999999999) {
      newErrors.push(
        new InputErrorModel<ApplicationVolumeDetails>({
          field: field,
          message:
            "Average Annual Volume must be between $1,000 and $999,999,999",
        }),
      );
    }
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ApplicationVolumeDetails>[] = [];
    ApplicationVolumeDetails.validateAvgAnnualVolume(
      "avgAnnualVolume",
      this.avgAnnualVolume,
      newErrors,
    );
    CoreInputModelUtil.validateNumber(
      parseInt(this.avgTicketAmount),
      "avgTicketAmount",
      newErrors,
      "Please enter a valid number",
    );
    CoreInputModelUtil.validateNumber(
      parseInt(this.highestTicketAmount),
      "highestTicketAmount",
      newErrors,
      "Please enter a valid number",
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIMerchantVolumeModel {
    return {
      averageAnnualVolume: parseInt(this.avgAnnualVolume),
      averageTicketAmount: parseInt(this.avgTicketAmount),
      highestTicketAmount: parseInt(this.highestTicketAmount),
    };
  }
}
export class ModeOfTransactions {
  inPerson: string;
  online: string;
  total: string;
  errors: InputErrorModel<ModeOfTransactions>[];

  constructor(data?: Partial<ModeOfTransactions>) {
    this.inPerson = data?.inPerson || "";
    this.online = data?.online || "";
    this.total = data?.total || "100";
    this.errors =
      (data &&
        data.errors?.map((e) => new InputErrorModel<ModeOfTransactions>(e))) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof ModeOfTransactions): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof ModeOfTransactions): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ModeOfTransactions>[] = [];
    CoreInputModelUtil.validateTextField(
      this.online,
      "online",
      newErrors,
      undefined,
      "Please enter value",
    );
    if (parseFloat(this.online) > 100) {
      newErrors.push({
        field: "online",
        message: "Must be ≤ 100",
      });
    }

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createAPIModel(): any {
    return {
      inPerson: parseInt(this.inPerson, 10) || 0,
      online: parseInt(this.online, 10) || 0,
    };
  }
}
export class MerchantAccountDetails {
  ownershipType: string;
  businessCategory: string;
  legalNameOfBusiness: string;
  mccCode: string;
  DBA?: string;
  taxId: string;
  phoneNumber: string;
  website: string;
  countryDetails: CountryWithStateModel;
  errors: InputErrorModel<MerchantAccountDetails>[];

  constructor(data?: Partial<MerchantAccountDetails>) {
    this.ownershipType = data?.ownershipType || "";
    this.businessCategory = data?.businessCategory || "";
    this.mccCode = data?.mccCode || "";
    this.legalNameOfBusiness = data?.legalNameOfBusiness || "";
    this.DBA = data?.DBA;
    this.taxId = data?.taxId || "";
    this.phoneNumber = data?.phoneNumber || "";
    this.website = data?.website || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.errors =
      (data &&
        data.errors?.map(
          (e) => new InputErrorModel<MerchantAccountDetails>(e),
        )) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof MerchantAccountDetails): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof MerchantAccountDetails): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  static validateLegalName = (
    value: string,
    field: keyof MerchantAccountDetails,
    errors: InputErrorModel<MerchantAccountDetails>[],
  ) => {
    if (!value) {
      errors.push(
        new InputErrorModel<MerchantAccountDetails>({
          field: field,
          message: "Please enter legal name of business",
        }),
      );
    } else if (value.length < 2 || value.length > 100) {
      errors.push(
        new InputErrorModel<MerchantAccountDetails>({
          field: field,
          message: "legal business name length must be between 2 to 100",
        }),
      );
    }
  };

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<MerchantAccountDetails>[] = [];
    CoreInputModelUtil.validateTextField(
      this.ownershipType,
      "ownershipType",
      newErrors,
      undefined,
      "Please select ownership type",
    );
    CoreInputModelUtil.validateTextField(
      this.businessCategory,
      "businessCategory",
      newErrors,
      undefined,
      "Please select business category",
    );
    CoreInputModelUtil.validateTextField(
      this.mccCode,
      "mccCode",
      newErrors,
      undefined,
      "Please select category",
    );
    MerchantAccountDetails.validateLegalName(
      this.legalNameOfBusiness,
      "legalNameOfBusiness",
      newErrors,
    );
    CoreInputModelUtil.validateTaxId("taxId", this.taxId, newErrors);
    CoreInputModelUtil.validatePhoneNumber(
      "phoneNumber",
      this.phoneNumber,
      newErrors,
    );
    CoreInputModelUtil.validateWebsite("website", this.website, newErrors);
    CoreInputModelUtil.validateDBA("DBA", this.DBA, newErrors);
    CoreInputModelUtil.validateTextField(
      this.countryDetails.code.toString(),
      "countryDetails",
      newErrors,
      undefined,
      "Please select country",
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createAPIModel(): any {
    return {
      ownershipType: this.ownershipType,
      category: this.businessCategory,
      mccCode: this.mccCode,
      dbaName: this.DBA || "",
      legalName: this.legalNameOfBusiness,
      ein: this.taxId.replace(/-/g, ""),
      website: this.website,
      phoneNumber: this.phoneNumber,
      countryId: this.countryDetails?.id || null,
      countryCode: this.countryDetails.code.toString() || "",
    };
  }
}
export interface BusinessDetails {
  id: string;
  name: string;
}
export interface IMerchantAccount {
  applicationStatus: ApplicationStatus;
  name: string;
  amountProcessed: number;
  createdDate: string;
  applicationId: string;
}
export class PurchaseMerchantAccountSettingsModel {
  name: string;
  applicationId: string;
  merchantAccountId: string;
  gateway: string;
  constructor(data?: Partial<PurchaseMerchantAccountSettingsModel>) {
    this.name = data?.name || "";
    this.applicationId = data?.applicationId || "";
    this.merchantAccountId = data?.merchantAccountId || "";
    this.gateway = data?.gateway || "";
  }

  isFiservMerchantAccount() {
    return this.gateway === "Fiserv";
  }

  isNMIMerchantAccount() {
    return this.gateway === "NMI";
  }
  createAPIModel(): IAPIPurchaseMerchantModel {
    return {
      gateway: this.gateway as keyof typeof MerchantAccountsGateway,
      applicationId: this.applicationId,
      merchantAccountId: this.merchantAccountId,
      name: this.name,
      collectJSKey: "",
    };
  }

  static deserializeAPIModel(data: IAPIPurchaseMerchantModel): MerchantAccount {
    if (!data) return new MerchantAccount();

    return MerchantAccount.deserializeAPIModel2(data);
  }
}

export class MerchantAccountWithPaySettingsModel {
  name: string;
  applicationId: string;
  merchantAccountId: string;
  gateway: string;
  imageURL: string;
  collectJsKey: string;
  paymentSettings: PaymentSettings;
  constructor(data?: Partial<MerchantAccountWithPaySettingsModel>) {
    this.name = data?.name || "";
    this.applicationId = data?.applicationId || "";
    this.merchantAccountId = data?.merchantAccountId || "";
    this.gateway = data?.gateway || "";
    this.collectJsKey = data?.collectJsKey || "";
    this.imageURL = data?.imageURL || "";
    this.paymentSettings = data?.paymentSettings
      ? new PaymentSettings(data.paymentSettings)
      : new PaymentSettings();
  }

  isFiservMerchantAccount() {
    return this.gateway === "Fiserv";
  }

  isNMIMerchantAccount() {
    return this.gateway === "NMI";
  }

  /**
   * Rate configured on the merchant account.
   *
   * Customer-facing screens must NOT use this: a Checkout / Purchase Link
   * charges the rate it was saved with, exposed as
   * CheckoutPaymentPageModel.effectiveSurchargePercentage. This stays for
   * anything that genuinely needs the account-level default.
   */
  surchargePercentage(): number {
    return parseFloat(this.paymentSettings.cards.surchargePercentage) || 0;
  }

  getMerchantModel(): MerchantAccount {
    return new MerchantAccount({
      id: this.merchantAccountId,
      name: this.name,
      applicationId: this.applicationId,
      gateway: this.gateway,
      collectJSKey: this.collectJsKey,
    });
  }

  createAPIModel(): IAPIMerchantWithPaySettingsModel {
    return {
      gateway: this.gateway as keyof typeof MerchantAccountsGateway,
      applicationId: this.applicationId,
      merchantAccountId: this.merchantAccountId,
      name: this.name,
      collectJSKey: this.collectJsKey,
      paymentSettings: this.paymentSettings.createAPIModel(),
    };
  }

  static deserializeAPIModel(
    data: IAPIMerchantWithPaySettingsModel,
  ): MerchantAccountWithPaySettingsModel {
    if (!data) return new MerchantAccountWithPaySettingsModel();
    return new MerchantAccountWithPaySettingsModel({
      name: data?.name ? data.name : "",
      applicationId: data.applicationId,
      merchantAccountId: data.merchantAccountId,
      gateway: data.gateway,
      paymentSettings: PaymentSettings.deserializeAPIModel(
        data.paymentSettings,
      ),
      collectJsKey: data.collectJSKey,
      imageURL: data.imageURL,
    });
  }
}
export class MerchantAccount {
  id: string;
  applicationStatus: ApplicationStatus;
  name: string;
  email: string;
  amountProcessed: number;
  createdDate: string;
  applicationId: string;
  merchantAccountId: string;
  gateway: string;
  statementDescriptor: string;
  firstPayment: MerchantPaymentModel;
  lastPayment: MerchantPaymentModel;
  totalProcessed: number;
  paymentSettings: PaymentSettings;
  collectJSKey: string;
  allowedPaymentMethods: MerchantAllowedPaymentMethodTypes;
  isDefault: boolean;
  errors: InputErrorModel<PaymentSettings>[];
  processorType: keyof typeof NMIProcessorTypes;
  constructor(data?: Partial<MerchantAccount>) {
    this.id = data?.id || "";
    this.applicationStatus =
      data?.applicationStatus || ApplicationStatus.Pending;
    this.name = data?.name || "";
    this.email = data?.email || "";
    this.amountProcessed = data?.amountProcessed || 0;
    this.createdDate = data?.createdDate || "";
    this.applicationId = data?.applicationId || "";
    this.merchantAccountId = data?.merchantAccountId || "";
    this.gateway = data?.gateway || "Fiserv";
    this.statementDescriptor = data?.statementDescriptor || "";
    this.firstPayment = new MerchantPaymentModel(data?.firstPayment);
    this.lastPayment = new MerchantPaymentModel(data?.lastPayment);
    this.totalProcessed = data?.totalProcessed || 0;
    this.paymentSettings = new PaymentSettings(data?.paymentSettings);
    this.allowedPaymentMethods = new MerchantAllowedPaymentMethodTypes();
    this.collectJSKey = data?.collectJSKey || "";
    this.isDefault = data?.isDefault || false;
    this.errors = data?.errors || [];
    this.processorType = data?.processorType || NMIProcessorTypes.Tsys;

    autorun(() => {
      this.validate(true);
    });
  }

  getLogo(): string {
    if (!this.isNMIMerchantAccount()) return MindMePaymentsLogo;

    switch (this.processorType) {
      case NMIProcessorTypes.Tsys:
        return TSYSLogo;
      case NMIProcessorTypes.ElavonEMV:
        return ElavonLogo;
      case NMIProcessorTypes.GlobalCollect:
      case NMIProcessorTypes.GlobalPaymentsEastEMV:
        return GlobalPaymentsLogo;
      case NMIProcessorTypes.FirstDataNashville:
      case NMIProcessorTypes.FirstDataNashvilleNorth:
      case NMIProcessorTypes.FirstDataNashvilleNorthV2:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleNorthEMV:
      case NMIProcessorTypes.FirstDataCanada:
      case NMIProcessorTypes.FirstDataOmaha:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleEMV:
      case NMIProcessorTypes.FirstDataRapidConnectOmahaEMV:
      case NMIProcessorTypes.FirstDataRapidConnectCardnetNorthEMV:
        return FiservLogo;
      default:
        return MindMePaymentsLogo;
    }
  }
  getClassName(): string {
    if (!this.isNMIMerchantAccount()) return "mindme-logo";

    switch (this.processorType) {
      case NMIProcessorTypes.Tsys:
        return "tsys-logo";
      case NMIProcessorTypes.ElavonEMV:
        return "elavon-logo";
      case NMIProcessorTypes.GlobalCollect:
      case NMIProcessorTypes.GlobalPaymentsEastEMV:
        return "global-collect-logo";
      case NMIProcessorTypes.FirstDataNashville:
      case NMIProcessorTypes.FirstDataNashvilleNorth:
      case NMIProcessorTypes.FirstDataNashvilleNorthV2:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleNorthEMV:
      case NMIProcessorTypes.FirstDataCanada:
      case NMIProcessorTypes.FirstDataOmaha:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleEMV:
      case NMIProcessorTypes.FirstDataRapidConnectOmahaEMV:
      case NMIProcessorTypes.FirstDataRapidConnectCardnetNorthEMV:
        return "fiserv-logo";
      default:
        return "mindme-logo";
    }
  }

  static getLogoByProcessorType(
    processorType: keyof typeof NMIProcessorTypes,
  ): string {
    switch (processorType) {
      case NMIProcessorTypes.Tsys:
        return TSYSLogo;
      case NMIProcessorTypes.ElavonEMV:
        return ElavonLogo;
      case NMIProcessorTypes.GlobalCollect:
      case NMIProcessorTypes.GlobalPaymentsEastEMV:
        return GlobalPaymentsLogo;
      case NMIProcessorTypes.FirstDataNashville:
      case NMIProcessorTypes.FirstDataNashvilleNorth:
      case NMIProcessorTypes.FirstDataNashvilleNorthV2:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleNorthEMV:
      case NMIProcessorTypes.FirstDataCanada:
      case NMIProcessorTypes.FirstDataOmaha:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleEMV:
      case NMIProcessorTypes.FirstDataRapidConnectOmahaEMV:
      case NMIProcessorTypes.FirstDataRapidConnectCardnetNorthEMV:
        return FiservLogo;
      default:
        return MindMePaymentsLogo;
    }
  }
  static getClassNameByProcessorType(
    processorType: keyof typeof NMIProcessorTypes,
  ): string {
    switch (processorType) {
      case NMIProcessorTypes.Tsys:
        return "tsys-logo";
      case NMIProcessorTypes.ElavonEMV:
        return "elavon-logo";
      case NMIProcessorTypes.GlobalCollect:
      case NMIProcessorTypes.GlobalPaymentsEastEMV:
        return "global-collect-logo";
      case NMIProcessorTypes.FirstDataNashville:
      case NMIProcessorTypes.FirstDataNashvilleNorth:
      case NMIProcessorTypes.FirstDataNashvilleNorthV2:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleNorthEMV:
      case NMIProcessorTypes.FirstDataCanada:
      case NMIProcessorTypes.FirstDataOmaha:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleEMV:
      case NMIProcessorTypes.FirstDataRapidConnectOmahaEMV:
      case NMIProcessorTypes.FirstDataRapidConnectCardnetNorthEMV:
        return "fiserv-logo";
      default:
        return "mindme-logo";
    }
  }

  static getLogo(
    gateway: keyof typeof MerchantAccountsGateway,
    processorType?: keyof typeof NMIProcessorTypes,
  ): string {
    if (gateway !== "NMI") return MindMePaymentsLogo;
    switch (processorType) {
      case NMIProcessorTypes.Tsys:
        return TSYSLogo;
      case NMIProcessorTypes.ElavonEMV:
        return ElavonLogo;
      case NMIProcessorTypes.GlobalCollect:
      case NMIProcessorTypes.GlobalPaymentsEastEMV:
        return GlobalPaymentsLogo;
      case NMIProcessorTypes.FirstDataNashville:
      case NMIProcessorTypes.FirstDataNashvilleNorth:
      case NMIProcessorTypes.FirstDataNashvilleNorthV2:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleNorthEMV:
      case NMIProcessorTypes.FirstDataCanada:
      case NMIProcessorTypes.FirstDataOmaha:
      case NMIProcessorTypes.FirstDataRapidConnectNashvilleEMV:
      case NMIProcessorTypes.FirstDataRapidConnectOmahaEMV:
      case NMIProcessorTypes.FirstDataRapidConnectCardnetNorthEMV:
        return FiservLogo;
      default:
        return MindMePaymentsLogo;
    }
  }

  goToMerchantPortal() {
    if (this.isFiservMerchantAccount()) {
      window.open(process.env.REACT_APP_MERCHANT_PORTAL, "_blank");
    } else if (this.isNMIMerchantAccount()) {
      window.open(process.env.REACT_APP_NMI_MERCHANT_PORTAL, "_blank");
    }
  }

  isFiservMerchantAccount() {
    return this.gateway === "Fiserv";
  }

  isNMIMerchantAccount() {
    return this.gateway === "NMI";
  }

  // hasError(field: keyof PaymentSettings): boolean {
  //   const paymentMethod = this.paymentSettings[field] as PaymentMethodDetails;
  //   return parseFloat(paymentMethod.surchargePercentage) > 4;
  // }

  hasError(field: keyof PaymentSettings): boolean {
    return this.errors.some((error) => error.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PaymentSettings>[] = [];

    Object.entries(this.paymentSettings).forEach(([key, value]) => {
      const field = key as keyof PaymentSettings;
      const paymentMethod = value as PaymentMethodDetails;

      /* Surcharge is only validated when the payment method is enabled and a
         surcharge is actually being applied. Turning the surcharge off keeps
         the last entered value visible in the UI while sending 0 to the API. */
      if (
        paymentMethod instanceof PaymentMethodDetails &&
        paymentMethod.isEnabled &&
        paymentMethod.surchargeEnabled
      ) {
        const surcharge = parseFloat(paymentMethod.surchargePercentage);
        if (
          isNaN(surcharge) ||
          surcharge < PaymentMethodDetails.minSurchargePercentage ||
          surcharge > PaymentMethodDetails.maxSurchargePercentage
        ) {
          newErrors.push(
            new InputErrorModel<PaymentSettings>({
              field,
              message: PaymentMethodDetails.surchargeRangeMessage,
            }),
          );
        }
      }
    });

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    return this.errors.length === 0;
  }

  static deserializeAPIModel(data: IAPIMerchantAccountModel): MerchantAccount {
    const mappedData: Partial<MerchantAccount> = {
      applicationStatus: data.applicationStatus,
      name: data.name,
      amountProcessed: data.amountProcessed,
      createdDate: data.createdDate,
      applicationId: data.applicationId,
      merchantAccountId: data.merchantAccountId,
      gateway: data.gateway,
      id: data.id,
      email: data.email,
      collectJSKey: data.collectJSKey || "",
      statementDescriptor: data.statementDescriptor,
      firstPayment:
        data.firstPayment &&
        new MerchantPaymentModel().deserializeAPIModel(data.firstPayment),
      lastPayment:
        data.lastPayment &&
        new MerchantPaymentModel().deserializeAPIModel(data.lastPayment),
      totalProcessed: data.totalProcessed,
      paymentSettings: data.paymentSettings
        ? PaymentSettings.deserializeAPIModel(data.paymentSettings)
        : new PaymentSettings(),
      processorType: data.processorType
        ? data.processorType
        : NMIProcessorTypes.Tsys,
      isDefault: data.isDefault || false,
    };
    return new MerchantAccount(mappedData);
  }

  static deserializeAPIModel2(
    data: IAPIPurchaseMerchantModel,
  ): MerchantAccount {
    const mappedData: Partial<MerchantAccount> = {
      name: data?.name || "",
      applicationId: data?.applicationId || "",
      merchantAccountId: data?.merchantAccountId || "",
      gateway: data?.gateway || "",
      collectJSKey: data?.collectJSKey || "",
      id: data?.id,
    };
    return new MerchantAccount(mappedData);
  }

  static deserializeToMerchantSettings(data: MerchantAccount) {
    return new PurchaseMerchantAccountSettingsModel({
      name: data.name,
      applicationId: data.applicationId,
      merchantAccountId: data.merchantAccountId,
      gateway: data.gateway,
    });
  }

  createAPIModel(): IAPIMerchantAccountModel {
    return {
      applicationStatus: this.applicationStatus,
      name: this.name,
      amountProcessed: this.amountProcessed,
      createdDate: this.createdDate,
      applicationId: this.applicationId,
      merchantAccountId: this.merchantAccountId,
      gateway: this.gateway as keyof typeof MerchantAccountsGateway,
      id: this.id,
      email: this.email,
      statementDescriptor: this.statementDescriptor,
      paymentSettings: this.paymentSettings?.createAPIModel() || null,
      firstPayment: this.firstPayment.createAPIModel(),
      lastPayment: this.lastPayment.createAPIModel(),
      totalProcessed: this.totalProcessed,
      collectJSKey: this.collectJSKey,
      isDefault: this.isDefault,
    };
  }

  /**
   * Default surcharge a Checkout or Purchase Link starts from. Callers copy
   * these values onto their own record, so a later edit to the merchant
   * account never rewrites a saved Checkout or Link.
   */
  surchargeDefaults(): {
    surchargeEnabled: boolean;
    surchargePercentage: string;
    surchargeAskCustomerFirst: boolean;
  } {
    return this.paymentSettings.surchargeDefaults();
  }

  createAPIModel2(): IAPIPurchaseMerchantModel {
    return {
      gateway: this.gateway as keyof typeof MerchantAccountsGateway,
      applicationId: this.applicationId,
      merchantAccountId: this.merchantAccountId,
      name: this.name,
      id: this.id,
      collectJSKey: this.collectJSKey,
    };
  }

  createPaymentSettingsAPIModel(): IAPIPaymentSetting {
    return {
      merchantAccount: {
        gateway: this.gateway as keyof typeof MerchantAccountsGateway,
        applicationId: this.applicationId,
        merchantAccountId: this.merchantAccountId,
        name: this.name,
        id: this.id,
        collectJSKey: this.collectJSKey,
      },
      cards: this.paymentSettings.cards.createAPIModel(),
      applePay: this.paymentSettings.applePay.createAPIModel(),
      googlePay: this.paymentSettings.googlePay.createAPIModel(),
      bankOrAch: this.paymentSettings.bankAch.createAPIModel(),
      checkOrWire: this.paymentSettings.checkWire.createAPIModel(),
      statementDescriptor: this.statementDescriptor,
      allowedPaymentMethods: this.allowedPaymentMethods.createAPIModel(),
    };
  }

  createGeneralSettingsAPIModel(
    data: GeneralSettingsModel,
  ): IAPIGeneralSettingsModel {
    return {
      countryId: data.country.id,
      timeZoneId: data.timeZone.id,
      dateFormat: data.dateFormat,
      numberFormat: data.numberFormat,
      currency: data.currency,
      phoneFormat: data.phoneFormat,
      preferredLanguage: data.language,
      accountId: AuthService.authData.accountId,
      isUseDayLightSavings: data.isUseDayLightSavings,
      includeCountry: data.includeCountry,
      categories: data.categories.map((category) => category.createAPIModel()),
    };
  }

  static getDefaultMerchantAccount(
    merchantAccounts: MerchantAccount[],
  ): MerchantAccount | undefined {
    const activeMerchantAccounts =
      merchantAccounts?.filter(
        (f) => f.applicationStatus === ApplicationStatus.Active,
      ) || [];

    const defaultMerchantAccount = activeMerchantAccounts.find(
      (x) => x.isDefault,
    );
    return (
      defaultMerchantAccount ||
      (activeMerchantAccounts?.length > 0
        ? activeMerchantAccounts[0]
        : undefined)
    );
  }
}

export class PaymentSettings {
  cards: PaymentMethodDetails;
  applePay: PaymentMethodDetails;
  googlePay: PaymentMethodDetails;
  bankAch: PaymentMethodDetails;
  checkWire: PaymentMethodDetails;

  constructor(data?: Partial<PaymentSettings>) {
    this.cards = data?.cards
      ? new PaymentMethodDetails(data?.cards)
      : new PaymentMethodDetails();
    this.applePay = data?.applePay
      ? new PaymentMethodDetails(data.applePay)
      : new PaymentMethodDetails();
    this.googlePay = data?.googlePay
      ? new PaymentMethodDetails(data.googlePay)
      : new PaymentMethodDetails();
    this.bankAch = data?.bankAch
      ? new PaymentMethodDetails(data.bankAch)
      : new PaymentMethodDetails();
    this.checkWire = data?.checkWire
      ? new PaymentMethodDetails({ ...data.checkWire, isEnabled: false })
      : new PaymentMethodDetails();
  }

  /**
   * Surcharge a Checkout / Purchase Link starts from, taken from Credit Card -
   * the only method that carries a surcharge today. A card method the merchant
   * has switched off cannot surcharge, so this arrives off even when a rate is
   * stored against it.
   */
  surchargeDefaults(): {
    surchargeEnabled: boolean;
    surchargePercentage: string;
    surchargeAskCustomerFirst: boolean;
  } {
    return {
      surchargeEnabled: this.cards.isEnabled && this.cards.surchargeEnabled,
      surchargePercentage: this.cards.surchargePercentage,
      surchargeAskCustomerFirst: this.cards.surchargeAskCustomerFirst,
    };
  }

  createAPIModel(): IAPIMerchantPaymentSettings {
    return {
      cards: this.cards.createAPIModel(),
      applePay: this.applePay.createAPIModel(),
      googlePay: this.googlePay.createAPIModel(),
      bankOrAch: this.bankAch.createAPIModel(),
      checkOrWire: this.checkWire.createAPIModel(),
    };
  }

  static deserializeAPIModel(data: IAPIMerchantPaymentSettings) {
    if (!data) return new PaymentSettings();
    return new PaymentSettings({
      /* Pass the whole cards object through so surchargeEnabled and
         askDonorFirst survive. isEnabled keeps defaulting to true when the
         API omits it, which is how cards behaved before it was sent. */
      cards: PaymentMethodDetails.deserializeAPIModel({
        ...data?.cards,
        isEnabled: data?.cards?.isEnabled ?? true,
        surchargePercentage: data?.cards?.surchargePercentage || 0.0,
      }),
      applePay: PaymentMethodDetails.deserializeAPIModel(data.applePay),
      googlePay: PaymentMethodDetails.deserializeAPIModel(data.googlePay),
      bankAch: PaymentMethodDetails.deserializeAPIModel(data.bankOrAch),
      checkWire: PaymentMethodDetails.deserializeAPIModel(data.checkOrWire),
    });
  }
}

export class PaymentMethodDetails {
  /**
   * Surcharge range accepted everywhere a surcharge can be set: Payment
   * Settings, the Checkout payment builder and the Purchase Link popup.
   */
  static readonly minSurchargePercentage = 0.1;
  static readonly maxSurchargePercentage = 4;
  static readonly defaultSurchargePercentage = "3.00";
  static readonly surchargeRangeMessage = "Value must be between 0.1% and 4%.";

  isEnabled: boolean;
  surchargePercentage: string;
  /** Model fields mirror the API field names one-for-one. */
  surchargeEnabled: boolean;
  surchargeAskCustomerFirst: boolean;

  constructor(data?: Partial<PaymentMethodDetails>) {
    this.isEnabled = data?.isEnabled ?? false;
    this.surchargePercentage = data?.surchargePercentage ?? "0.00";
    this.surchargeEnabled =
      data?.surchargeEnabled ??
      parseFloat(this.surchargePercentage || "0") > 0;
    /* Never defaults to true: an API value of false, or no value at all,
       leaves "Ask customer first" off. */
    this.surchargeAskCustomerFirst = data?.surchargeAskCustomerFirst ?? false;
  }

  static deserializeAPIModel(
    data?: IAPIMerchantPaymentMethodDetails,
  ): PaymentMethodDetails {
    if (!data) return new PaymentMethodDetails();

    return new PaymentMethodDetails({
      isEnabled: data.isEnabled ?? false,
      surchargePercentage: (data.surchargePercentage ?? 0).toFixed(2),
      /* Records saved before surchargeEnabled existed only encoded "on" as a
         percentage above zero. */
      surchargeEnabled:
        data.surchargeEnabled ?? (data.surchargePercentage ?? 0) > 0,
      surchargeAskCustomerFirst:
        data.surchargeAskCustomerFirst ?? data.askDonorFirst ?? false,
    });
  }

  createAPIModel(): IAPIMerchantPaymentMethodDetails {
    return {
      isEnabled: this.isEnabled,
      /* surchargeEnabled carries the on/off state, so the rate the merchant
         configured is kept as typed instead of being zeroed when switched off. */
      surchargePercentage: parseFloat(this.surchargePercentage || "0") || 0,
      surchargeEnabled: this.isEnabled && this.surchargeEnabled,
      surchargeAskCustomerFirst: this.surchargeAskCustomerFirst,
    };
  }
}
export class MerchantPaymentModel {
  amount: number;
  date: Date | null;
  merchantAccount: PurchaseMerchantAccountSettingsModel;
  isRefund: boolean;
  constructor(data?: Partial<MerchantPaymentModel>) {
    this.amount = data?.amount || 0;
    this.date = data?.date || null;
    this.merchantAccount = new PurchaseMerchantAccountSettingsModel(
      data?.merchantAccount,
    );
    this.isRefund = data?.isRefund || false;
  }

  deserializeAPIModel(data: IAPIMerchantPaymentModel): MerchantPaymentModel {
    return new MerchantPaymentModel({
      amount: data.amount,
      date: UtilityMethods.removeZFromDateString(data.dateProcessed),
      merchantAccount:
        data.merchantAccount &&
        PurchaseMerchantAccountSettingsModel.deserializeAPIModel(
          data.merchantAccount,
        ),
      isRefund: data.isRefund,
    });
  }

  createAPIModel(): IAPIMerchantPaymentModel {
    return {
      amount: this.amount,
      dateProcessed: this.date?.toISOString() || "",
      merchantAccount: this.merchantAccount.createAPIModel(),
      isRefund: this.isRefund,
    };
  }
}
export class MCCCodeModel {
  code: string;
  description: string;
  industry: string;
  constructor(data?: Partial<MCCCodeModel>) {
    this.code = data?.code || "";
    this.description = data?.description || "";
    this.industry = data?.industry || "";
  }

  static deserializeAPIModel(data: IAPIMCCCodeResponseModel): MCCCodeModel {
    return new MCCCodeModel({
      code: data.code,
      description: data.description,
      industry: data.industry,
    });
  }
}
